import Button from '@mui/material/Button';
import Grid from '@mui/material/GridLegacy';
import type { PopoverOrigin } from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import autobind from 'autobind-decorator';
import classnames from 'classnames';
import type { ComponentType, MouseEvent } from 'react';
import React, { useMemo, Component } from 'react';

import { ArrowDown, ArrowUp } from '../../icons/Arrows';
import { getTrace, withTrace } from '../../utils/trace';
import type { MenuItemConfig } from '../Menu';
import { AttachedMenu, Menu } from '../Menu';
import { useSelectedLabelWidth, useStyleOnePaperWidth, useStyles } from './SelectMenu.styles';
import type {
    MenuValue,
    SelectMenuAllProps,
    SelectMenuContentProps,
    SelectMenuItem,
    SelectMenuProps,
    SelectMenuState,
} from './SelectMenu.types';

const applyTrace = getTrace('select-menu');

const selectMenuItem = (label: string | ComponentType<any>, key: string): ComponentType<any> => {
    if (typeof label === 'string') {
        return () => (
            <Typography color="inherit" variant="body2" {...applyTrace(`menu-item-${key}`)}>
                {label}
            </Typography>
        );
    }
    return label;
};

function SelectedLabel(props: {
    classes: any;
    disabled: boolean;
    labelPrefix: string | null;
    label: string | ComponentType;
    labelWidth?: number;
}) {
    const { label: Label, classes, disabled, labelPrefix, labelWidth = 0 } = props;
    const { classes: selectedLabelWidthClass } = useSelectedLabelWidth({ labelWidth });
    if (typeof Label === 'string') {
        return (
            <Typography
                noWrap
                align="right"
                color="inherit"
                variant="body2"
                {...applyTrace('item-label')}
                className={classnames(
                    { [classes.disabledSelected]: disabled },
                    { [selectedLabelWidthClass.maxLabelWidth]: labelWidth },
                )}
            >
                {labelPrefix ? `${labelPrefix}${Label}` : Label}
            </Typography>
        );
    }
    return (
        <Grid
            className={classnames(
                { [classes.disabledLabel]: disabled },
                { [selectedLabelWidthClass.maxLabelWidth]: labelWidth },
            )}
        >
            <Label {...applyTrace('item-label-component')} />
        </Grid>
    );
}

const generateMenuItems = (
    options: SelectMenuItem[],
    selected: string | number | undefined,
    action: (item: string | number) => void,
): MenuItemConfig[] =>
    options.map(
        ({
            key,
            label,
            ItemComponent = selectMenuItem(label, key.toString()),
        }: SelectMenuItem) => ({
            key,
            ItemComponent,
            label,
            selected: selected ? key === selected : false,
            action: () => action(key),
        }),
    );

const getValueOrDefault = (value: MenuValue, defaultValue: MenuValue) => {
    return defaultValue === undefined ? value : defaultValue;
};

const GenerateDropdown = (props: SelectMenuContentProps) => {
    const {
        options,
        optionGroups,
        disabled = false,
        fitContent = false,
        applyTrace,
        applyTraceMessage,
        buttonFullWidth = true,
        anchorRight = false,
        defaultValue, // Component will behave like an uncontrolled component
        value, // Component will behave like a controlled component
        open,
        selectedValue,
        anchorEl,
        anchorReference,
        handleSelection,
        toggleOpen,
        menuType = '2',
        setWidth = 0,
        labelWidth = 0,
        popoverClasses,
        popperClassName,
        labelPrefix = null,
        shouldAttachMenuToAnchor = false,
        popperPlacement = 'bottom-start',
        annotationsClassName,
        actionButton,
        customHeight,
    } = props;
    const { classes } = useStyles(undefined, { props: { classes: props.classes } });
    const menuItems = useMemo(
        () => generateMenuItems(options, getValueOrDefault(value, defaultValue), handleSelection),
        [options, defaultValue, value, handleSelection],
    );
    const showMenu = open && !disabled;
    const transformOrigin1 = anchorRight ? { horizontal: 'right', vertical: 'top' } : undefined;
    const transformOrigin2 = { vertical: 'top', horizontal: 'left' };

    const transformOrigin = menuType === '2' ? transformOrigin2 : transformOrigin1;
    const offsetFromRight = 21;
    const anchorStyle2 =
        anchorRight && anchorEl
            ? ({
                  horizontal: anchorEl.offsetWidth - offsetFromRight,
                  vertical: 'top',
              } as PopoverOrigin)
            : undefined;

    const anchorStyle1 = { vertical: 'bottom', horizontal: 'left' } as PopoverOrigin;
    const anchorOrigin = menuType === '2' ? anchorStyle2 : anchorStyle1;
    const justifySpacing = menuType === '2' ? 'flex-start' : 'space-between';
    const width = setWidth;

    let applyTraceMessageValue = '';

    if (applyTraceMessage) {
        applyTraceMessageValue = `${applyTraceMessage}-`;
    }

    const MenuComponent = shouldAttachMenuToAnchor ? AttachedMenu : Menu;
    const Icon = showMenu ? ArrowUp : ArrowDown;

    const { classes: styleOnePaperWidthClass } = useStyleOnePaperWidth({ width });

    return (
        <Grid
            container
            justifyContent="flex-end"
            className={classnames({
                [classes.fitContent]: fitContent,
            })}
        >
            <Grid container item xs>
                <Button
                    fullWidth={buttonFullWidth}
                    variant="text"
                    onClick={toggleOpen}
                    className={classnames({
                        [classes.button]: menuType === '2',
                        [classes.styleOneButton]: menuType === '1',
                        [classes.styleOneSelected]: menuType === '1' && showMenu,
                        [styleOnePaperWidthClass.setWidth]: setWidth !== 0,
                    })}
                    disabled={disabled}
                    {...applyTrace(`${applyTraceMessageValue}btn`)}
                >
                    <Grid
                        container
                        wrap="nowrap"
                        alignItems="center"
                        justifyContent={justifySpacing}
                        spacing={1}
                        className={classnames({ [classes.selected]: menuType === '2' && showMenu })}
                    >
                        <Grid
                            item
                            container
                            alignItems="center"
                            xs
                            className={classes.labelContainer}
                        >
                            <SelectedLabel
                                classes={classes}
                                disabled={disabled}
                                label={selectedValue}
                                labelPrefix={labelPrefix}
                                labelWidth={labelWidth}
                            />
                        </Grid>
                        <Grid item>
                            <Icon color="inherit" fontSize="small" className={classes.icon} />
                        </Grid>
                    </Grid>
                </Button>
            </Grid>
            <MenuComponent
                open={showMenu}
                options={menuItems}
                optionGroups={optionGroups}
                onClose={toggleOpen}
                classes={{
                    paper: classnames({
                        [classes.paper]: menuType === '2',
                        [classes.styleOnePaper]: menuType === '1',
                        [styleOnePaperWidthClass.menuWidth]: menuType === '1',
                    }),
                    menuItem: classnames(
                        { [classes.styleOneMenuItem]: menuType === '1' },
                        classes.menuItem,
                        annotationsClassName,
                    ),
                    menuLabel: classes.menuLabel,
                }}
                disableAutoFocusItem
                anchorEl={anchorEl}
                anchorReference={anchorReference}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                popoverClasses={popoverClasses}
                popperClassName={popperClassName}
                popperPlacement={popperPlacement}
                actionButton={actionButton}
                customHeight={customHeight}
                {...applyTrace('menu')}
            />
        </Grid>
    );
};

export class SelectMenuBase extends Component<SelectMenuAllProps, SelectMenuState> {
    constructor(props: SelectMenuAllProps) {
        super(props);
        const { defaultValue, value } = this.props;
        const { label = '' } = this.optionByKey(getValueOrDefault(value, defaultValue)) || {};
        this.state = {
            selectedValue: label,
            open: false,
            anchorEl: null,
            anchorReference: 'none',
        };
    }

    componentDidUpdate(prevProps: SelectMenuAllProps) {
        const { defaultValue } = this.props;
        if (prevProps.defaultValue !== defaultValue) {
            const { label = '' } = this.optionByKey(defaultValue) || {};
            this.setState({
                selectedValue: label,
            });
        }
    }

    @autobind
    toggleOpen(event: MouseEvent<HTMLElement>) {
        const { currentTarget: anchorEl = null } = event || {};
        const { open } = this.state;
        let anchorReference: 'none' | 'anchorEl' | 'anchorReference' = 'none';

        if (anchorEl && !open) {
            anchorReference = 'anchorEl';
        }

        this.setState({ anchorEl, anchorReference, open: !open && anchorEl !== null });
    }

    optionByKey(findKey?: string | number | null): SelectMenuItem | undefined {
        const { options } = this.props;
        return options.find(({ key }) => key === findKey);
    }

    @autobind
    handleSelection(selected: string | number) {
        const { defaultValue, onSelect } = this.props;
        // Set the new value in uncontrolled mode only
        if (this.checkUncontrolled(defaultValue)) {
            const { label = '' } = this.optionByKey(selected) || {};
            this.setState({ selectedValue: label, open: false });
        } else {
            this.setState((prevState) => ({ ...prevState, open: false }));
        }
        onSelect(selected);
    }

    checkUncontrolled(defaultValue: string | number | undefined) {
        return defaultValue !== undefined;
    }

    render() {
        const { open, selectedValue, anchorEl, anchorReference } = this.state;

        return (
            <GenerateDropdown
                {...{
                    ...this.props,
                    open,
                    selectedValue,
                    anchorEl,
                    anchorReference,
                    toggleOpen: this.toggleOpen,
                    handleSelection: this.handleSelection,
                }}
            />
        );
    }
}

export const SelectMenu = withTrace('select-menu')<SelectMenuProps>(SelectMenuBase);
